import { assembleRespondents } from '@helpers/assembleRespondents';
import { handleChange } from '@helpers/handleChange';
import { StoreState } from '@interfaces/stores';
import { invitationsService } from '@services/domain-factories/invitations';
import { responsesService } from '@services/domain-factories/responses';
import { rolesService } from '@services/domain-factories/roles';
import { usersService } from '@services/domain-factories/users';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import {
  Collaborator,
  InvitationEnvelope,
  Member,
  SurveyResponse,
  UserGroup
} from '@vincerosoft/assessfluence-types';

type Actions = {
  handleChange: <T extends boolean>(
    change: T extends true ? string : InvitationEnvelope,
    isDelete?: T extends true ? T : never
  ) => void;
  handleResponseChange: (response: SurveyResponse, isDelete?: boolean) => void;
  initInvitations: (member: Member, userGroups: UserGroup[]) => Promise<void>;
  initCollaboratorInvitations: (memberId: string) => Promise<void>;
  getResponses: (invitationIds: string[]) => Promise<void>;
  getRespondents: () => Promise<void>;
};

const baseState = {
  isInitialized: false,
  loading: false,
  responses: null as SurveyResponse[] | null,
  inFlightInit: null as Promise<void> | null,
  respondents: null as SurveyResponse['respondent'][] | null
};

export const initialInvitationsState: StoreState['invitations'] = {
  invitations: null as unknown as InvitationEnvelope[],
  collaboratorAssociations: null as unknown as Collaborator[],
  collaboratorInvitations: null as unknown as InvitationEnvelope[],
  referenceData: null
};

export const invitationsStore = createStoreWithSelector<
  StoreState['invitations'] & typeof baseState & Actions
>()((set, get) => ({
  ...initialInvitationsState,
  ...baseState,

  getResponses: async (invitationIds) => {
    const responses = await responsesService.getResponsesByInvitationEnvIds(invitationIds);
    set({ responses });
  },

  getRespondents: async () => {
    const storedRespondents = get().respondents;
    if (storedRespondents) {
      return;
    }

    const roles = await rolesService.getRoles();

    const { members, users } = await usersService.getUsersAndMembersToJoin<
      {
        id: string;
        user_id: string;
        status: Member['status'];
      },
      {
        id: string;
        email: string;
        name: string;
        uid: string;
      }
    >(roles, () => {}, {
      memberSelection: ['id', 'user_id', 'status'],
      userSelection: ['id', 'email', 'name', 'uid']
    });
    const joinedData = assembleRespondents(members, users);
    set({ respondents: joinedData });
  },

  initCollaboratorInvitations: async (memberId) => {
    const inFlight = await invitationsService.loadCollaboratorInvitations(memberId);
    set({
      collaboratorAssociations: inFlight.collaborators,
      collaboratorInvitations: inFlight.invitations
    });
  },

  initInvitations: (member, userGroups) =>
    initOnce(
      'isInitialized',
      set,
      get,
      () =>
        invitationsService.loadInvitationsStore(member, userGroups, (invitations) =>
          set({ invitations })
        ),
      (results) => {
        const { invitations, ...referenceData } = results;
        set({ referenceData, invitations, isInitialized: true });
      }
    ),

  handleResponseChange: (response, isDelete) =>
    set((state) => ({
      responses: handleChange(state.responses || [], response, isDelete)!
    })),

  handleChange: (change, isDelete) =>
    set((state) => ({
      invitations: handleChange(state.invitations || [], change, isDelete)!
    }))
}));

export const useInvitationsStore = createUseStore(invitationsStore);
export const getInvitationsState = () => invitationsStore.getState();
